import React, { useState, useEffect } from 'react';
import { Layout, Card, Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Typography, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Title } = Typography;
const { Option } = Select;

const ChartOfAccountsPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/accounting/accounts');
            if (res.data.success) {
                setAccounts(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลผังบัญชีได้');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingItem) {
                await api.put(`/api/accounting/accounts/${editingItem.id}`, values);
                message.success('แก้ไขบัญชีเรียบร้อย');
            } else {
                await api.post('/api/accounting/accounts', values);
                message.success('เพิ่มบัญชีเรียบร้อย');
            }
            setIsModalVisible(false);
            form.resetFields();
            setEditingItem(null);
            fetchAccounts();
        } catch (error) {
            message.error('ทำรายการไม่สำเร็จ');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/api/accounting/accounts/${id}`);
            message.success('ลบรายการเรียบร้อย');
            fetchAccounts();
        } catch (error) {
            message.error('ลบรายการไม่สำเร็จ');
        }
    };

    const columns = [
        {
            title: 'รหัสบัญชี',
            dataIndex: 'CODE',
            key: 'CODE',
            sorter: (a, b) => (a.CODE || '').localeCompare(b.CODE || '')
        },
        {
            title: 'ชื่อบัญชี',
            dataIndex: 'NAME',
            key: 'NAME'
        },
        {
            title: 'ประเภท',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const types = {
                    asset: { label: 'สินทรัพย์', color: 'blue' },
                    liability: { label: 'หนี้สิน', color: 'red' },
                    equity: { label: 'ส่วนของเจ้าของ', color: 'purple' },
                    income: { label: 'รายได้', color: 'green' },
                    expense: { label: 'ค่าใช้จ่าย', color: 'orange' }
                };
                const t = types[type] || { label: type, color: 'default' };
                return <Tag color={t.color}>{t.label}</Tag>;
            }
        },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => {
                        setEditingItem(record);
                        form.setFieldsValue({
                            code: record.CODE,
                            name: record.NAME,
                            type: record.type
                        });
                        setIsModalVisible(true);
                    }} />
                    <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDelete(record.id)} okText="ใช่" cancelText="ไม่">
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout style={{ padding: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={2}>ผังบัญชี (Chart of Accounts)</Title>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchAccounts}>รีเฟรช</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            setEditingItem(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}>เพิ่มบัญชีใหม่</Button>
                    </Space>
                </div>

                <Card>
                    <Table
                        columns={columns}
                        dataSource={accounts}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>
            </Space>

            <Modal
                title={editingItem ? "แก้ไขผังบัญชี" : "เพิ่มผังบัญชีใหม่"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="code" label="รหัสบัญชี" rules={[{ required: true, message: 'กรุณาระบุรหัสบัญชี' }]}>
                        <Input placeholder="เช่น 1101" />
                    </Form.Item>
                    <Form.Item name="name" label="ชื่อบัญชี" rules={[{ required: true, message: 'กรุณาระบุชื่อบัญชี' }]}>
                        <Input placeholder="เช่น เงินฝากธนาคาร" />
                    </Form.Item>
                    <Form.Item name="type" label="ประเภทบัญชี" rules={[{ required: true, message: 'กรุณาเลือกประเภทบัญชี' }]}>
                        <Select placeholder="เลือกประเภท">
                            <Option value="asset">สินทรัพย์ (Asset)</Option>
                            <Option value="liability">หนี้สิน (Liability)</Option>
                            <Option value="equity">ส่วนของเจ้าของ (Equity)</Option>
                            <Option value="income">รายได้ (Income)</Option>
                            <Option value="expense">ค่าใช้จ่าย (Expense)</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ChartOfAccountsPage;
